import { keccak256, parseAbi, zeroAddress, type Address, type Hex } from "viem";
import type { launchRpc } from "./launchpad";

// Both deployments and manager bindings were read on chain 99901, 2026-11-04.
// See web/docs/plans/token-detail.md for the route and deployment evidence.
const memeQuoter = "0x379f556c5a748bd7cc9baf0f7a344094ac1814f5";
const pairQuoter = "0x1ad2186a2734dc8715dea55967df823fd6e6fea4";
const adapter = "0xf285ed7a48fd962fd4a24b536a2b63da0c7b3782";
const codeHashes = {  
  [memeQuoter]: "0x469aa0c619f9e32d2e75aaf4e6d86c2d3ddeb43349936a912b4e0bd49fef7e9f",
  [pairQuoter]: "0x90aab4f8680a5c6ef89831d4d6747cc43d4629b03926f37b07af5fa355c03e47",
  [adapter]: "0x97e06e09233e3f88edd29daa89cadd5e3d6d762e698bc476c1b0e0898d77d1a6",
} as const;
const abi = parseAbi([
  "function quoteExactInputSingle(((address currency0,address currency1,uint24 fee,int24 tickSpacing,address hooks) poolKey,bool zeroForOne,uint128 exactAmount,bytes hookData) params) returns (uint256 amountOut,uint256 gasEstimate)",
]);
type PoolKey = { currency0: Address; currency1: Address; fee: number; tickSpacing: number; hooks: Address };
type Rpc = typeof launchRpc;
const verified = new WeakMap<Rpc, Map<Address, Promise<void>>>();

function verifyCode(rpc: Rpc, address: keyof typeof codeHashes, blockNumber: bigint) {
  let cache = verified.get(rpc);
  if (!cache) { cache = new Map(); verified.set(rpc, cache); }
  const saved = cache.get(address);
  if (saved) return saved;
  // Once per RPC client, during quoting only. A failed read remains retryable.
  const pending = rpc.getCode({ address, blockNumber }).then(code => {
    if (!code || keccak256(code) !== codeHashes[address]) throw new Error("Sell quoter deployment does not match the configured route.");
  }).catch((error: unknown) => { cache.delete(address); throw error; });
  cache.set(address, pending);
  return pending;
}

async function quote(rpc: Rpc, address: Address, poolKey: PoolKey, zeroForOne: boolean, exactAmount: bigint, blockNumber: bigint) {
  if (exactAmount <= 0n || exactAmount >= 2n ** 128n) throw new Error("Amount is outside the sell quoter range.");
  const { result: [amountOut] } = await rpc.simulateContract({
    address, abi, functionName: "quoteExactInputSingle", account: zeroAddress, blockNumber,
    args: [{ poolKey, zeroForOne, exactAmount, hookData: "0x" as Hex }],
  });
  if (amountOut <= 0n) throw new Error("No sell output is available.");
  return amountOut;
}

export async function quoteLaunchpadSell(rpc: Rpc, key: PoolKey, token: Address, quoteToken: Address,
  amount: bigint, blockNumber: bigint) {
  const native = quoteToken.toLowerCase() === zeroAddress;
  await Promise.all([
    verifyCode(rpc, memeQuoter, blockNumber),
    ...(!native ? [verifyCode(rpc, pairQuoter, blockNumber), verifyCode(rpc, adapter, blockNumber)] : []),
  ]);
  const pairOut = await quote(rpc, memeQuoter, key, key.currency0.toLowerCase() === token.toLowerCase(), amount, blockNumber);
  if (native) return pairOut;
  // Matches the pinned adapter's key builder: native / quote, 5%, spacing 100,
  // no hook. The second hop uses the first hop's output at the same block.
  return quote(rpc, pairQuoter, { currency0: zeroAddress, currency1: quoteToken,
    fee: 50_000, tickSpacing: 100, hooks: zeroAddress }, false, pairOut, blockNumber);
}
